import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, OnInit, Output } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MessageService } from "primeng/api";
import { catchError, forkJoin, of } from "rxjs";
import { ZorroTableComponent } from "../../../../components/common/custom-table/zorro-table.component";
import { LoadingComponent } from "../../../../components/common/loading/loading.component";
import { DeleteConfirmationComponent } from "../../../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { SharedComponents } from "../../../../components/shared.module";
import { ConfigService } from "../../../../core/services/config/config.service";
import { RestTcoService } from "../../../../core/services/rest-tco.service";
import { TcoProjectService } from "../../../../core/services/tco-project/tco-project.service";
import { ToasterService } from "../../../../core/services/toaster-service";
import { UtilitiesService } from "../../../../core/services/utilities.service";
import { MaterialModule } from "../../../../core/shared/material.module";
import { modelExecutionRecordColumns } from "../../../../core/table-templates/model-execution-record-table";
import { MessageEnum } from "../../../../core/utils/message.enum";

/**
 * Component responsible for handling and displaying model execution records.
 * Manages loading state while fetching data and populates the component with records.
 */
@Component({
  selector: "app-model-execution",
  standalone: true,
  imports: [
    CommonModule,
    ZorroTableComponent,
    LoadingComponent,
    ...SharedComponents,
    MaterialModule,
  ],
  templateUrl: "./model-execution.component.html",
  styleUrl: "./model-execution.component.scss",
})
export class ModelExecutionComponent implements OnInit {
  records: any = {
    columns: modelExecutionRecordColumns,
  };
  isLoading = false;
  extractedRealm: string | null;
  selectedRows: any = [];
  isFirstTimeVisit = true;
  previousSelectedRow: any;
  isDisabled = true;
  isRemoveModelDisabled: boolean = true;
  isProcessing: boolean = false;
  selectedFileData: any;
  modelId: any = '';
  private modalService = inject(NgbModal);
  private messageService = inject(MessageService);

  /**
   * Emits an event to move to the next tab.
   * @type {EventEmitter<void>}
   */
  @Output() moveToNextTabEvent = new EventEmitter<void>();
  @Output() moveToPreviousTabEvent = new EventEmitter<void>();
  @Output() enableTabsEvent = new EventEmitter<number[]>();
  @Output() disableTabsEvent = new EventEmitter<number[]>();
  tableData: any;
  reversedTableData: any;

  constructor(
    private restTcoService: RestTcoService,
    private configService: ConfigService,
    private utilityService: UtilitiesService,
    private tcoProjectService: TcoProjectService,
    private readonly toaster: ToasterService
  ) {
    this.extractedRealm = this.configService.getConfig("isDomainResolver")
      ? this.utilityService?.extractRealmFromUrl()
      : sessionStorage.getItem("selectedRealm");
  }

  /**
   * Lifecycle hook that initializes the component and loads the data.
   */
  ngOnInit() {
    this.loadModalExecutions();
  }

  /**
   * Simulates loading of data by setting the loading state to true, then populating records after a delay.
   * This function can be adapted to load actual data from an API or other sources.
   */
  loadModalExecutions() {
    this.isLoading = true;
    this.restTcoService.getAllModelExecutions().subscribe(
      (item) => {
        this.tableData = item;
        this.reverseTableData();
        item?.forEach((element: any) => {
          if (typeof element.source === 'number') {
            element.source = this.utilityService.convertFileSize(element.source);
          }
          element.publishDate = new Date(element.publishDateTime);
        });
        // this.records.rows = item;

        this.records.rows = this.reversedTableData?.sort(
          (a: any, b: any) =>
            b.publishDate.getTime() - a.publishDate.getTime()
        )
          .map((item: any, index: number) => ({
            ...item,
            serial_number: index + 1,
          }));

        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
      }
    );
  }

  reverseTableData() {
    this.reversedTableData = this.tableData.slice().reverse();
  }

  navigateToResults() {
    console.log(" this.modelId", this.modelId)
    this.isProcessing = true;
    if (this.modelId) {
      this.restTcoService.getResultsModelById(this.modelId).subscribe(
        (res) => {
          if (res.totalTestCasesCount == 0) {
            const lastModalObj = this.selectedRows[0];
            // const lastModalObj = this.records?.rows[this.records.rows?.length - 1];
            const requestBody = {
              tenantId: this.extractedRealm,
            };
            this.isLoading = true;
            this.restTcoService
              .sendToUniqueTestCases(lastModalObj?.id, requestBody)
              .subscribe(
                (item) => {
                  console.log("item", item);
                  if (item?.message === "SUCCESS") {
                    const itemData = item;
                    const allRequests = [
                      this.restTcoService
                        .saveAllSegmentationData(itemData?.segmentation_data)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .sendAllTestcases(itemData?.duplicate_testcases)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .saveAllUniquesInDuplicates(itemData?.unique_in_duplicate)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .saveAllInitalRetainTestcases(itemData?.unique_in_duplicate)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .sendAllTestcasesOverlappings(itemData?.overlapping_testcases)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .sendAllTestcasesOverlappingCombinations(
                          itemData?.overlapping_combinations
                        )
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .saveAllTestcases(itemData?.duplicates_in_overlaps_combinations)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .saveAllDiscardTestcases(
                          itemData?.discards_in_overlaps_combinations
                        )
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .sendAllObsoleteTestcases(itemData?.obsolete_testcases)
                        .pipe(catchError((err) => of(err))),
                      this.restTcoService
                        .sendAllUniquetestcase(itemData?.unique_testcases)
                        .pipe(catchError((err) => of(err))),
                    ];

                    forkJoin(allRequests).subscribe({
                      next: (results) => {
                        this.deleteAllFilesInPythonDirectory();
                        this.tcoProjectService.setModalId(lastModalObj?.id);
                        //Emit event to enable the Results tab (index 3 in tabs array)
                        this.enableTabsEvent.emit([3]);
                        this.moveToNextTabEvent.emit();
                        //  Disable Segmentation Threshold tab (index 1)
                        this.disableTabsEvent.emit([1]);
                        this.isLoading = false;
                        this.isProcessing = false;
                      },
                      error: (err) => {
                        this.isLoading = false;
                        this.isProcessing = false;
                      },
                    });
                  }
                },
                (error) => {
                  this.isLoading = false;
                  this.isProcessing = false;
                }
              );
          }
          else {
            this.tcoProjectService.setModalId(this.modelId);
            //Emit event to enable the Results tab (index 3 in tabs array)
            this.enableTabsEvent.emit([3]);
            this.moveToNextTabEvent.emit();
            //  Disable Segmentation Threshold tab (index 1)
            this.disableTabsEvent.emit([1]);
            this.isLoading = false;
            this.isProcessing = false;
          }
        },
        (error) => {
          this.isLoading = false;
          this.isProcessing = false;
        }
      );
    }

  }

  deleteAllFilesInPythonDirectory() {
    const requestBody = {
      tenantId: this.extractedRealm,
    };
    this.restTcoService.deleteAllFilesFromDirectory(requestBody).subscribe(
      (response) => {
        console.log(response);
      },
      (error) => {
        console.log(error);
      }
    );
  }

  handleRowSelection(event: any) {
    this.selectedRows = event.data;
    const selectedModel = this.selectedRows[0];
    this.modelId = selectedModel?.id;
    this.tcoProjectService.setSelectedFile(this.selectedRows[0]);
    if (this.selectedRows !== this.previousSelectedRow) {
      this.previousSelectedRow = this.selectedRows;
      this.isFirstTimeVisit = true;
    }

    this.isRemoveModelDisabled = this.selectedRows?.length === 0;

    if (this.selectedRows?.length === 1) {
      this.isDisabled = false;
    } else {
      this.isDisabled = true;
    }
  }

  removeModel() {
    const deletedIds = this.selectedRows.map((element: any) => element.id);
    const deleteObservables = this.selectedRows.map((element: any) =>
      this.restTcoService.deleteModel(element?.id)
    );
    forkJoin(deleteObservables).subscribe({
      next: () => {
        this.toaster.pop({ type: 'success', text: MessageEnum.MODEL_DELETE_SUCCESS });
        this.records.rows = this.records.rows.filter(
          (item: any) => !deletedIds.includes(item.id)
        );
        this.records.rows = this.records.rows.map((item: any, index: number) => ({
          ...item,
          serial_number: index + 1,
        }));
        this.selectedRows = [];
        this.isDisabled = true;
        this.isRemoveModelDisabled = true;
      },
      error: (error) => {
        this.toaster.pop({ type: 'error', text: MessageEnum.MODEL_DELETE_FAILURE });
        console.error("Error deleteing modal:", error);
      }
    });
  }
    openDeleteConfirmation() {
      const modalRef = this.modalService.open(DeleteConfirmationComponent, {
        windowClass:"delete-confirmation-window",
        centered: true,
        backdrop: 'static',
      });
  
      modalRef.componentInstance.data = {
        title: 'Confirm Deletion',
        description: 'Are you sure you want to delete the selected Model?',
      };
  
      modalRef.result
        .then((confirmed) => {
          if (confirmed) {
            this.removeModel();
          }
        })
        .catch(() => {
    });
  }
  goBack() {
    this.moveToPreviousTabEvent.emit();
  }
}
